import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerCancelledTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildBookingDetailState } from '../../utils/bookingNavigation';
import { GETBookings } from '../../services/booking';
import { IBooking } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import DateFilterComp, {
  DateFilterData,
} from '../../components/ui/filters/enquiry';
import { CancelledList } from '../../components/ui/list/Cancelled';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const Cancelled = () => {
  const location = useLocation();
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } = usePaginationParams();

  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);

  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [tabValue, setTabValue] = useState<string>(
    () => searchParams.get('tab') ?? 'TODAYS_CANCELLED',
  );

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchBookings = async (searchQuery?: string, sort?: 'ASC' | 'DESC') => {
    try {
      setIsLoading(true);

      const response = await GETBookings({
        page: 1,
        limit: 100000,
        searchQuery,
        sortOrder: sort,
        status: GetTabFilter(),
      });

      setBookings(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(searchQuery, sort);
  }, [searchQuery, sort, location]);

  const filteredBookings = useMemo(() => {
    let list = bookings || [];

    if (filterFlag) {
      list = list.filter((booking: IBooking) => {
        const bookingDate = getBookingCancelDate(booking);
        if (!bookingDate) return false;

        const bookingTime = bookingDate.getTime();

        if (startDate && endDate) {
          return (
            bookingTime >= getStartOfDay(startDate).getTime() &&
            bookingTime <= getEndOfDay(endDate).getTime()
          );
        }

        if (startDate) {
          return bookingTime >= getStartOfDay(startDate).getTime();
        }

        if (endDate) {
          return bookingTime <= getEndOfDay(endDate).getTime();
        }

        return true;
      });

      return list;
    }

    return list.filter((booking: IBooking) => {
      const bookingDate = getBookingCancelDate(booking);
      if (!bookingDate) return false;

      return checkBookingByTab(bookingDate, tabValue);
    });
  }, [bookings, tabValue, startDate, endDate, filterFlag]);

  const paginatedBookings = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return filteredBookings.slice(startIndex, endIndex);
  }, [filteredBookings, page, rowsPerPage]);

  const reloadDefaultList = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    setPage(1);
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleOpenManageDrawer = (id: string | number) => {
    navigate(`/bookings/${id}`, {
      state: buildBookingDetailState('cancelled', {
        returnSearch: location.search,
      }),
    });
  };

  const handleTabChange = (event: any, newValue: string) => {
    setFilterFlag(false);
    setStartDate(null);
    setEndDate(null);
    setTabValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', newValue);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const handleAdd = () => {
    navigate(`/bookings/create`);
  };

  const handleFilterRefresh = (Filter: DateFilterData) => {
    setPage(1);

    if (Filter.startDate && Filter.endDate) {
      setStartDate(getStartOfDay(new Date(Filter.startDate)));
      setEndDate(getEndOfDay(new Date(Filter.endDate)));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(getStartOfDay(new Date(Filter.startDate)));
      setEndDate(null);
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setStartDate(null);
      setEndDate(getEndOfDay(new Date(Filter.endDate)));
      setFilterFlag(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        <SmartFilter
          title="Cancelled Job"
          createFilter={['SEARCH', 'ADD']}
          filterHeader={'Filter Services'}
          addHeader={'Add Services'}
          addbuttontitle="Add"
          filterElement={
            <DateFilterComp
              tabValue={tabValue}
              onResetFilter={reloadDefaultList}
              startDate={
                startDate
                  ? new Date(startDate).toISOString().split('T')[0]
                  : null
              }
              endDate={
                endDate ? new Date(endDate).toISOString().split('T')[0] : null
              }
            />
          }
          onNavigate={handleAdd}
          onSearch={handleSearchRefresh}
          onFiltered={handleFilterRefresh}
          addDrawerSize="md"
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
          <Box display="flex" justifyContent="center" width="100%" my={2}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={tabStyles}>
              <Tab value={'TODAYS_CANCELLED'} label="Today Cancelled" />
              <Tab value={'OLD_CANCELLED'} label="Old Cancelled" />
              <Tab value={'ALL_CANCELLED'} label="All Cancelled Enquires" />
            </Tabs>
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              height: '100%',
              maxHeight: '100%',
              overflowY: 'scroll',
              scrollbarWidth: 'none',
            }}
          >
            {filteredBookings.length <= 0 && !isLoading ? (
              <Typography textAlign={'center'} marginTop={'20%'}>
                No Cancelled Bookings Found
              </Typography>
            ) : (
              <CancelledList
                headerCells={headerCancelledTable}
                bookings={paginatedBookings}
                isLoading={isLoading}
                onManageClick={handleOpenManageDrawer}
              />
            )}
          </Box>

          <Box display={'block'} bottom={0} right={0}>
            <Pagination
              count={filteredBookings.length}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default withComponentAuth(Cancelled);

export const tabStyles = {
  backgroundColor: '#1B1B1B',
  borderRadius: '8px',
  padding: '6px',
  display: 'inline-flex',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    color: '#fff',
    textTransform: 'none',
    fontSize: '14px',
    padding: '10px 16px',
    minWidth: 'unset',
  },
  '& .Mui-selected': {
    color: '#fff !important',
    borderRadius: '8px',
    backgroundColor: '#d32f2f',
  },
};

const getStartOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const getEndOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const getBookingCancelDate = (booking: any): Date | null => {
  const date =
    booking?.cancelledAt ||
    booking?.cancelDate ||
    booking?.cancelledDate ||
    booking?.updatedAt ||
    booking?.createdAt;

  if (!date) return null;

  return new Date(date);
};

const checkBookingByTab = (bookingDate: Date, tabValue: string) => {
  const today = new Date();

  const todayStart = getStartOfDay(today);
  const todayEnd = getEndOfDay(today);

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const beforeYesterday = new Date(today);
  beforeYesterday.setDate(today.getDate() - 2);

  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(today.getDate() - 3);

  const bookingTime = bookingDate.getTime();

  switch (tabValue) {
    case 'TODAYS_CANCELLED':
      return (
        bookingTime >= todayStart.getTime() && bookingTime <= todayEnd.getTime()
      );

    case 'OLD_CANCELLED':
      return (
        bookingTime >= getStartOfDay(beforeYesterday).getTime() &&
        bookingTime <= getEndOfDay(yesterday).getTime()
      );

    case 'ALL_CANCELLED':
      return bookingTime <= getEndOfDay(threeDaysAgo).getTime();

    default:
      return true;
  }
};

const GetTabFilter = () => {
  return 'CANCELLED';
};
